<template>
    <div class="ealm-root">
        <div class="ealm-header">
            <h1>Early Adopter Localisation Map</h1>
        </div>
        <div class="ealm-body">
            <div class="ealm-left" ref="leftPane">
                <div class="ealm-picker">
                    <label class="ealm-label">Project</label>
                    <DxSelectBox :items="projectItems"
                                 display-expr="Title"
                                 value-expr="TopicId"
                                 :show-clear-button="true"
                                 :search-enabled="true"
                                 :search-mode="'contains'"
                                 v-model:value="selectedProjectId"
                                 placeholder="Select a Project" />
                </div>
                <div class="ealm-meta" v-if="selectedProject">
                    <div class="ealm-meta-title">{{ selectedProject.Title }}</div>
                    <div class="ealm-meta-desc" v-if="selectedProject.TpcDescription">
                        {{ selectedProject.TpcDescription }}
                    </div>
                </div>
                <div class="ealm-hint" v-if="!selectedProjectId">
                    Pick a Project to visualize Early Adopter locations.
                </div>
                <div class="ealm-loading" v-if="isLoading">
                    <DxLoadIndicator :visible="true" :height="20" :width="20" />
                    <span>Loading stakeholders…</span>
                </div>
                <div class="ealm-hint warn" v-if="errorMessage">
                    {{ errorMessage }}
                </div>
                <div class="ealm-hint warn" v-if="!isLoading && selectedProjectId && !hasAnySelection && !errorMessage">
                    No Early Adopters with country information found for this project.
                </div>
                <div class="ealm-stakeholder-list" v-if="connectedStakeholders.length" ref="stakeholderList">
                    <div class="ealm-list-title">Early Adopters ({{ connectedStakeholders.length }})</div>
                    <ul class="ealm-stakeholder-ul">
                        <li v-for="s in connectedStakeholders" :key="s.TopicId" @click="focusStakeholder(s)" class="clickable-item">
                            {{ s.Title }} <span v-if="s.HQCountry">({{ s.HQCountry }})</span>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="ealm-right">
                <MapLibreCoverageMap ref="coverageMap"
                                     :selected-countries-dict="selectedCountriesDict"
                                     :aspect-ratio="2"
                                     :bottom-safe-margin="16"
                                     data-mode="external"
                                     @resized="onMapResized" />
            </div>
        </div>
    </div>
</template>

<script>import axios from 'axios';
import DxSelectBox from 'devextreme-vue/select-box';
import DxLoadIndicator from 'devextreme-vue/load-indicator';
import MapLibreCoverageMap from './widgets/MapLibreCoverageMap.vue';

export default {
  name: 'EarlyAdopterLocalisationMap',
  components: { DxSelectBox, MapLibreCoverageMap, DxLoadIndicator },
  data() {
    return {
      projectItems: [],
      selectedProjectId: null,
      selectedCountriesDict: { iso3: [], names: [] },
      isLoading: false,
      errorMessage: '',
      connectedStakeholders: [],
      mapHeightPx: 0,
      resolvedArgsByTopic: {},
    };
  },
  computed: {
    countryMapNameIso3() { return this.$store.getters.countryMapNameIso3; },
    countryMapIso3Name() { return this.$store.getters.countryMapIso3Name; },
    selectedProject() {
      if (!this.selectedProjectId) return null;
      return this.projectItems.find(p => p.TopicId === this.selectedProjectId) || null;
    },
    hasAnySelection() {
      const d = this.selectedCountriesDict || {};
      return (d.iso3?.length || 0) + (d.names?.length || 0) > 0;
    },
  },
  mounted() {
    try { document.title = 'Early Adopter Localisation Map'; } catch {/* ignore */}
    this.ensureLoadPanel();
    this.loadCountryList();
    this.loadProjects();
  },
  methods: {
    loadCountryList() { return this.$store.dispatch('loadCountryList'); },
    ensureLoadPanel() {
      // Ensure window.LoadPanel is available and safe to call for store actions
      // The store calls window.LoadPanel.Show() which requires DevExtreme dxLoadPanel
      // If not properly initialized, we stub it with no-op functions
      try {
        // Test if the LoadPanel widget is actually initialized
        const $el = window.$ && window.$('#GlobalLoadingPanel');
        if ($el && $el.length && typeof $el.dxLoadPanel === 'function') {
          // Try to get the instance - if it fails, the widget isn't initialized
          try {
            $el.dxLoadPanel('instance');
            // Widget exists and is initialized, LoadPanel should work
            return;
          } catch {
            // Widget not initialized, need to stub
          }
        }
      } catch {
        // jQuery or element not available
      }
      
      // Stub LoadPanel with no-op functions to prevent errors
      window.LoadPanel = {
        Show: () => {},
        Hide: () => {},
        Initialize: () => {},
        Level: 0,
        Locked: false,
        ShowOnSubmit: false,
      };
    },
    _canonName(s) {
      try {
        return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      } catch { return String(s || '').toLowerCase().trim(); }
    },
    async loadProjects() {
      // Load projects (ttyId=24, stgId=15 for published)
      const filter = {
        ttyId: 24,
        rouId: 0,
        stgId: 15, // Published
        excludeStgId: 0,
        statusId: 0,
        onlyAssignedTopics: false,
        onlyOwnedTopics: false,
        showBookmarkFilter: false,
        showRoundFilter: false,
        showExpertsModeSwitch: false,
        showOwnerStatus: false,
        showStatusFilter: false,
        showAddNew: false,
        useFullTextSearch: false,
        onlyPublishedInFilter: false,
        filterTtyId: 0,
        filterOnlyMy: false,
        filterTltId: 0,
        filterStgId: 0,
        filterTitle: '',
        columns: ['TopicId', 'Title', 'TpcDescription'],
        columnsConfig: [],
        hiddenColumns: [],
      };
      const ok = await this.$store.dispatch('getTopicList', filter);
      if (ok === true) {
        const list = this.$store.state.topicStore?.topicList || [];
        this.projectItems = list.map(it => ({
          ...it,
          Title: it.Title || it.title || it.TpcTitle || `#${it.TopicId}`,
        }));
      }
    },
    async loadStakeholders() {
      this.selectedCountriesDict = { iso3: [], names: [] };
      this.connectedStakeholders = [];
      this.errorMessage = '';
      
      if (!this.selectedProjectId) {
        this.isLoading = false;
        return;
      }
      
      this.isLoading = true;
      try {
        // Use GetEsaTopicLinkListEx endpoint which includes TliCategoryId (stakeholder role)
        const url = 'WebApi.asmx/api/topicLinkService/GetEsaTopicLinkListEx';
        const formData = new FormData();
        formData.append('topicId', JSON.stringify(this.selectedProjectId));
        
        const res = await axios.post(url, formData);
        
        // Check for API errors
        if (res?.data?.error || res?.data?.message) {
          this.errorMessage = res.data.error || res.data.message || 'API error occurred';
          return;
        }
        
        if (!res?.data?.success) {
          this.errorMessage = 'Failed to load topic links: ' + (res?.data?.errorMessage || 'Unknown error');
          return;
        }
        
        const linkList = res.data.topicLinkList;
        
        if (!linkList) {
          return;
        }

        // TopicList1 and TopicList2 contain arrays of link type groups
        // Each group has Topics array with the linked topics
        const list1 = Array.isArray(linkList.TopicList1) ? linkList.TopicList1 : [];
        const list2 = Array.isArray(linkList.TopicList2) ? linkList.TopicList2 : [];
        
        // Flatten the structure - each entry in TopicList1/2 is a group with Topics array
        const allLinks = [];
        for (const group of [...list1, ...list2]) {
          if (group && Array.isArray(group.Topics)) {
            allLinks.push(...group.Topics);
          } else if (group && group.TopicId) {
            // Direct topic object
            allLinks.push(group);
          }
        }

        // Filter for stakeholders (ttyId=16 or check TtyName contains 'stakeholder')
        const allStakeholders = allLinks.filter(link => {
          const ttyName = (link.TtyName || '').toLowerCase();
          const isStakeholder = ttyName.includes('stakeholder') || link.TtyId === 16;
          return isStakeholder;
        });
        
        // Filter for Early Adopters only (TdgList001Name = "Early Adopter")
        const stakeholders = allStakeholders.filter(link => {
          const role = (link.TdgList001Name || '').toLowerCase();
          return role === 'early adopter';
        });
        
        if (stakeholders.length === 0) {
          return;
        }

        // Get stakeholder topic IDs
        const stakeholderIds = stakeholders.map(s => s.TopicId);
        
        // OPTIMIZATION: Load country mapping ONCE from the first stakeholder
        let countryMapping = {}; // Value ID -> Country Name
        
        const firstStakeholderId = stakeholderIds[0];
        const argsToken = await this.ensureArgsForTopic(firstStakeholderId);
        const elements = await this.fetchGenericFormElements(firstStakeholderId, argsToken);
        
        let firstStakeholderCountryValue = null;
        if (Array.isArray(elements)) {
          const countryField = elements.find(e => (e.Caption || '').toLowerCase().includes('country'));
          if (countryField && Array.isArray(countryField.SelectBoxItems)) {
            // Build mapping from Value to Text
            for (const item of countryField.SelectBoxItems) {
              countryMapping[item.Value] = item.Text;
            }
            firstStakeholderCountryValue = countryField.Value;
          }
        }

        // OPTIMIZATION: Fetch all stakeholder topics in BULK with TdgLisId001 (country field)
        const bulkFilter = {
          ttyId: 16, // Stakeholder type
          rouId: 0,
          stgId: 0,
          excludeStgId: 0,
          statusId: 0,
          onlyAssignedTopics: false,
          onlyOwnedTopics: false,
          showBookmarkFilter: false,
          showRoundFilter: false,
          showExpertsModeSwitch: false,
          showOwnerStatus: false,
          showStatusFilter: false,
          showAddNew: false,
          useFullTextSearch: false,
          onlyPublishedInFilter: false,
          filterTtyId: 0,
          filterOnlyMy: false,
          filterTltId: 0,
          filterStgId: 0,
          filterTitle: '',
          columns: ['TopicId', 'Title', 'TdgLisId001'], // Include country list ID field
          columnsConfig: [],
          hiddenColumns: [],
          topicIds: stakeholderIds, // Filter to only these stakeholders
        };
        
        // Use store to fetch stakeholder topics (LoadPanel is ensured in mounted)
        const bulkOk = await this.$store.dispatch('getTopicList', bulkFilter);
        let stakeholderTopics = [];
        if (bulkOk === true) {
          stakeholderTopics = this.$store.state.topicStore?.topicList || [];
        }
        
        // Create a map of topicId -> country value from bulk data
        const countryValueByTopicId = {};
        for (const t of stakeholderTopics) {
          if (t.TdgLisId001) {
            countryValueByTopicId[t.TopicId] = t.TdgLisId001;
          }
        }
        
        // Build stakeholders with countries
        const stakeholdersWithCountry = stakeholders.map(s => {
          const countryValue = countryValueByTopicId[s.TopicId];
          const countryName = countryValue ? (countryMapping[countryValue] || '') : '';
          return {
            TopicId: s.TopicId,
            Title: s.TpcTitle || s.Title || `Stakeholder #${s.TopicId}`,
            HQCountry: countryName,
          };
        });
        
        // If bulk fetch didn't work, fall back to first stakeholder's data
        if (stakeholderTopics.length === 0 && firstStakeholderCountryValue) {
          stakeholdersWithCountry[0].HQCountry = countryMapping[firstStakeholderCountryValue] || '';
        }

        this.connectedStakeholders = stakeholdersWithCountry;

        // Build country dictionary
        const dict = { iso3: [], names: [] };
        const setIso3 = new Set();
        const setNames = new Set();

        this.connectedStakeholders.forEach(s => {
          let hq = String(s.HQCountry || '').trim();
          if (!hq) return;

          // Parse country name or ISO3
          const baseName = hq.replace(/\s*\([^)]*\)\s*$/, '').trim();
          if (baseName) setNames.add(baseName);

          // Check for ISO3 in parentheses
          const m = hq.match(/\(([^)]+)\)\s*$/);
          if (m && m[1]) {
            const code = String(m[1]).trim().toUpperCase();
            if (/^[A-Z]{3}$/.test(code)) setIso3.add(code);
          }

          // Resolve via CountryService
          const canon = this._canonName(baseName);
          const iso3FromDb = this.countryMapNameIso3?.[canon];
          if (iso3FromDb) setIso3.add(iso3FromDb);
        });

        dict.iso3 = Array.from(setIso3);
        dict.names = Array.from(setNames);
        this.selectedCountriesDict = dict;
      } catch {
        this.errorMessage = 'An error occurred while loading stakeholders';
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGenericFormElements(topicId, argsToken) {
      const applicationServiceUri = 'WebApi.asmx/api/applicationService';
      const fd = new FormData();
      fd.append('topicId', topicId);
      if (argsToken) fd.append('args', argsToken);
      try {
        const res = await axios.post(`${applicationServiceUri}/GetGenericFormData`, fd);
        if (res?.data?.success) return res.data.formElements || [];
        return [];
      } catch { return []; }
    },
    async ensureArgsForTopic(topicId) {
      if (this.resolvedArgsByTopic && this.resolvedArgsByTopic[topicId]) {
        return this.resolvedArgsByTopic[topicId];
      }
      try {
        const topicServiceUri = 'WebApi.asmx/api/topicService';
        const res = await axios.get(`${topicServiceUri}/GetTopicArgs`, { params: { topicId } });
        const token = res?.data?.args || '';
        if (token) {
          this.resolvedArgsByTopic[topicId] = token;
          return token;
        }
      } catch { /* ignore */ }
      return '';
    },
    onMapResized(h) {
      this.mapHeightPx = h || 0;
    },
    focusStakeholder(s) {
      if (s && s.HQCountry && this.$refs.coverageMap && typeof this.$refs.coverageMap.flyToCountry === 'function') {
        this.$refs.coverageMap.flyToCountry(s.HQCountry);
      }
    },
  },
  watch: {
    selectedProjectId() {
      this.loadStakeholders();
    },
  },
};</script>

<style scoped>
    .ealm-root {
        width: 100%;
    }

    .ealm-header {
        margin: 10px 0 20px;
    }

    .ealm-body {
        display: grid;
        grid-template-columns: 360px 1fr;
        gap: 24px;
    }

    .ealm-left {
        display: grid;
        gap: 16px;
        align-content: start;
    }

    .ealm-right {
        min-width: 0;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
    }

    .ealm-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
    }

    .ealm-meta-title {
        font-weight: 600;
    }

    .ealm-meta-desc {
        color: #666;
        font-size: 13px;
        white-space: pre-line;
    }

    .ealm-hint {
        color: #666;
        font-size: 13px;
    }

        .ealm-hint.warn {
            color: #a94442;
        }

    .ealm-stakeholder-list {
        margin-top: 12px;
        max-height: 400px;
        overflow-y: auto;
    }

    .ealm-list-title {
        font-weight: 600;
        margin-bottom: 6px;
    }

    .ealm-stakeholder-ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

        .ealm-stakeholder-ul li {
            font-size: 13px;
            line-height: 1.6;
            padding: 4px 0;
        }
        .ealm-stakeholder-ul li.clickable-item {
            cursor: pointer;
            transition: color 0.2s;
        }
        .ealm-stakeholder-ul li.clickable-item:hover {
            color: #1e90ff;
            text-decoration: underline;
        }

    .ealm-loading {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: #666;
        font-size: 13px;
    }
</style>